import type { ReactNode } from "react";
import dayjs from "dayjs";
import dayjsUtcPlugin from "dayjs/plugin/utc";
import type { User } from "../apiClient/models/User";

dayjs.extend(dayjsUtcPlugin);

const formatDateInUtcTimeZone = (
  date: string | Date | null,
  formatStr = "YYYY-MM-DD"
): string | null => {
  if (date === null) {
    return null;
  }
  return dayjs.utc(date).format(formatStr);
};

const formatIsoDateStr = (dateString: string): string => {
  const dt = new Date(dateString);
  return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const hasFeatureFlag = (user: User | null, featureFlag: string): boolean =>
  user?.feature_flags?.some(({ name }) => name === featureFlag) ?? false;

const hasRole = (user: User | null, role: string): boolean =>
  user?.roles?.some(({ name }) => name === role) ?? false;

const titleCase = (str: string, ignoreUnderscores = false): string =>
  str
    .toLowerCase()
    .split(ignoreUnderscores ? /\s+/ : /[_|\s]/)
    .filter((x) => x.length > 0)
    .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
    .join(" ");

const hexToRgb = (hex: string): number[] => {
  const rgb = hex
    .replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      (_match, r: string, g: string, b: string) => `#${r}${r}${g}${g}${b}${b}`
    )
    .substring(1)
    .match(/.{2}/g);

  if (!rgb) {
    return [0, 0, 0];
  }

  return rgb.map((x) => parseInt(x, 16));
};

const calcDateDuration = (
  start: string | Date,
  end: string | Date,
  highPrecision = false
): string => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const duration = endDate.getTime() - startDate.getTime();

  if (duration < 60000) {
    const seconds = Math.floor(duration / 1000);
    return `${seconds} sec`;
  }

  if (duration < 3600000) {
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return highPrecision ? `${minutes} min ${seconds} sec` : `${minutes} min`;
  }

  const hours = Math.floor(duration / 3600000);
  const minutes = Math.floor((duration % 3600000) / 60000);
  const seconds = Math.floor((duration % 60000) / 1000);
  const hourText = hours === 1 ? "hr" : "hrs";

  if (highPrecision) {
    return `${hours} ${hourText} ${minutes} min ${seconds} sec`;
  }

  return hours > 0 && minutes > 0
    ? `${hours} ${hourText} ${minutes} min`
    : `${hours} ${hourText}`;
};

const stringToNumber = (str: string): number => {
  return str.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
};

const zip = <T, U>(a: T[], b: U[]): Array<[T, U]> => a.map((k, i) => [k, b[i]]);

export const DEBOUNCE_DELAY = 500;

const formatUtcDate = (dateString: string | Date): string => {
  const date = new Date(dateString);
  return `${date.toUTCString()}`;
};

const formatDateRange = (
  startDateString: string,
  endDateString: string
): ReactNode => {
  const startDate = new Date(startDateString);
  const endDate = new Date(endDateString);

  const sameDay = startDate.toDateString() === endDate.toDateString();

  const formatTime = (date: Date): string => {
    return `${date.getUTCHours()}:${date
      .getUTCMinutes()
      .toString()
      .padStart(2, "0")}:${date.getUTCSeconds().toString().padStart(2, "0")}`;
  };

  const formatDate = (date: Date): string => {
    return `${date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })}`;
  };

  if (sameDay) {
    return (
      <>
        {formatDate(startDate)}
        <br />
        {`${formatTime(startDate)} - ${formatTime(endDate)} GMT`}
      </>
    );
  }

  const startDay = startDate.getUTCDate();
  const endDay = endDate.getUTCDate();
  const month = startDate.toLocaleString("en-US", { month: "long" });
  const year = startDate.getUTCFullYear();

  return (
    <>
      {`${month} ${startDay}-${endDay}, ${year}`}
      <br />
      {`${formatTime(startDate)} - ${formatTime(endDate)} GMT`}
    </>
  );
};

const asArray = <T,>(stringOrArrayOrFalsey: T | T[] | null | undefined): T[] =>
  ([] as T[]).concat(stringOrArrayOrFalsey || []);

const displayPercent = (percent: number): string => `${(percent * 100).toFixed(2)}%`;

const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const highlightMatch = (text: string, searchTerm: string): ReactNode => {
  if (!searchTerm) return `"${text}"`;

  const escapedSearchTerm = escapeRegExp(searchTerm);
  const regex = new RegExp(`(${escapedSearchTerm})`, "gi");

  return (
    <span className="text-md text-grey-200">
      &quot;
      {text
        .split(regex)
        .map((part, i) =>
          part.toLowerCase() === searchTerm.toLowerCase() ? (
            <strong key={i}>{part}</strong>
          ) : (
            part
          )
        )}
      &quot;
    </span>
  );
};

const hash = (s: string): number => {
  let h = 0;
  let i = 0;
  const l = s.length;
  if (l > 0) {
    while (i < l) h = ((h << 5) - h + s.charCodeAt(i++)) | 0;
  }
  return h;
};

const convertTimestampToSeconds = (timestamp: string | number): number => {
  if (typeof timestamp === "number") {
    return timestamp;
  }

  if (/^\d+(\.\d+)?$/.test(timestamp)) {
    return Number(timestamp);
  }

  const [hours, minutes, seconds] = timestamp.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export {
  zip,
  hash,
  formatDateInUtcTimeZone,
  formatIsoDateStr,
  hasFeatureFlag,
  titleCase,
  hasRole,
  hexToRgb,
  calcDateDuration,
  stringToNumber,
  formatUtcDate,
  asArray,
  displayPercent,
  highlightMatch,
  convertTimestampToSeconds,
  formatDateRange,
};
